<template>
  <article v-editable="blok" :class="{ 'without-page-banner': !pageBanner }">
    <component
      :is="pageBannerComponent"
      v-if="pageBanner"
      :blok="pageBanner"
      :comments-button="!hideComments"
      class="article-wrapper"
      metabuttons
    />
    <section v-if="hasRichTextContent" class="block section-md">
      <div class="container">
        <div class="row flex-wrap justify-around">
          <div class="column sm-11 md-5">
            <TagsList
              :audiences="audiences"
              :show-all="true"
              :topics="topics"
              size="lg"
            />
            <NewsMeta
              :author="author"
              :published-at="displayDate"
              :states="states"
            />
            <ArticleDownloads :downloads="downloads" />
          </div>
          <div class="column sm-11 md-7 mobile-order-first tablet-order-first">
            <div class="page-content">
              <header v-if="!pageBanner">
                <NavigationBreadcrumbs />
                <h1 v-if="title">
                  {{ title }}
                </h1>
                <h2
                  v-if="hasArticleIntroduction"
                  class="h3 light whitespace-pre-line"
                >
                  {{ introduction }}
                </h2>
              </header>
              <RichTextRenderer :document="richTextContent" />
              <NewsCallToAction :link="link" :link-label="linklabel" />
              <LazyVisibleArticleToolButtons
                :comments-button="!hideComments"
                :introduction="introduction"
                :title="title"
                hydrate-on-visible
              />
              <NewsletterPopupTrigger />
            </div>
          </div>
        </div>
      </div>
    </section>
    <HyvorCommentsForum
      v-if="!hideComments"
      :page-id="pageId"
      layout="reactions-only"
    />
    <component
      :is="block.component"
      v-for="block in blocks"
      :key="block._uid"
      :story="block"
    />
    <!-- Related content block -->
    <BlockRelatedContent :blok="blok" />
  </article>
</template>

<script setup lang="ts">
import type { NewsStoryblok } from '~/types/storyblok'

const props = defineProps<{ blok: NewsStoryblok }>()
const {
  title,
  introduction,
  pageBanner,
  pageBannerComponent,
  blocks,
  audiences,
  topics,
  states,
  downloads,
  hasArticleIntroduction,
  hasRichTextContent,
  richTextContent,
  hideComments,
  pageId,
} = useStoryContent(toRefs(props))
const { displayDate } = usePage()
const link = computed(() => props.blok?.link)
const linklabel = computed(() => props.blok?.link_label)
const author = computed(() => props.blok.author)
</script>

<style lang="less" scoped>
.article-tool-buttons {
  margin: @gutter-xxl 0 0;
  padding-top: @gutter-lg;
  border-top: solid @2px @color-border;

  :deep(.button-group) {
    .justify-center();
  }
}

@media print {
  article {
    section.block {
      .container {
        .row {
          .column:first-child {
            margin-top: @gutter-lg;
            flex-basis: 100%;
            border: solid @2px @color-border;
            padding: @gutter @gutter-sm;

            > * {
              max-width: none;
            }

            :deep(ul.icon-list) {
              li {
                padding: 0;
              }
            }
          }
        }
      }
    }
  }
}
</style>
